// TODO: move to common component folder; used by guest manager, virtual events, edit pages

import React, { Suspense } from 'react';
import { useSelector } from 'react-redux';
import cx from 'classnames';
import PropTypes from 'prop-types';

// Utils
import {
  selectDefaultValues,
  selectDraftOrSavedToApply,
} from 'selectors/website/customizationSelectors';
import { StyledPublicWrapper } from 'pages/Public.styles';
import styles from 'pages/Public.module.less';

import importImages from 'util/webpack';
import {
  mapWeddingAccountToWebsiteDateProp,
  mapWebsitePreviewWeddingValuesToWebsiteDateProp,
} from 'util/websiteDateUtil';
import { checkStyleObjectDataValid } from 'pages/publicStyleUtils/utils';
import { formatWeddingDate, formatLocation } from '../util/previewUtils';

// Components
import PublicPagesNav from '../../../PublicWebsite/PublicPagesNav';
import PublicHeader from '../../../PublicWebsite/PublicHeader';
import PublicFooter from '../../../PublicWebsite/PublicFooter';
import * as PAGE_COMPONENTS from './helper';

const previewImages = importImages(
  require.context('../../../../assets/images/icons/', false, /\.(jpg|jpeg|png|gif|svg)(\?.*)?$/)
);

const WebsitePreview = ({
  activePage,
  isHome,
  values,
  pages,
  wedding,
  account,
  weddingValues: weddingValuesProp,
  pageComponentKey,
  eventFormData = null,
  showStickyPreview,
  hideCustomization,
}) => {
  const weddingValues = weddingValuesProp || {};
  const CurrentPage = activePage ? PAGE_COMPONENTS[pageComponentKey] : null;
  const pageData = Object.assign({}, activePage, values);

  // hacky for now, but when the title is passed, we are in a preview NOT on the customization dashboard
  // TODO: re-factor when WebsitePreview logic cleaned up
  const date = isHome && weddingValues.title ? weddingValues.date : wedding.wedding_date;
  const weddingDate = formatWeddingDate(date);

  /*
   There are 3 layers of styles we need to take acount of for Website customization

   1. The base theme. Set up by the <Helmet /> component
   2. customizationValues. Values the couple have overriden, and have been saved on the BE
   3. customizationDraft. Values that have been selected in the form, but have NOT been saved on the BE

   The above list is from least to most specific, i.e. values selected on Customization Dashboard but are not saved take priority
  */
  const defaultValues = useSelector(selectDefaultValues);
  const customizationValues = useSelector(selectDraftOrSavedToApply);

  // data that uses live form values
  const formData = {
    title: isHome ? weddingValues.title : wedding.title,
    location: isHome
      ? formatLocation(weddingValues.city, weddingValues.stateProvince)
      : formatLocation(wedding.city, wedding.state_province),
    hashtag: isHome ? weddingValues.hashtag : wedding.hashtag,
    weddingDate,
    city: isHome ? weddingValues.city : null,
    stateProvince: isHome ? weddingValues.stateProvince : null,
  };

  const liveNavPages = activePage
    ? pages.map(page => {
        if (page.type !== activePage.type) return page;
        const title = isHome ? values.nav_title : values.title;
        return { ...page, nav_title: title || page.nav_title };
      })
    : null;

  const renderPublicHeader = () => {
    const websiteDateProp =
      mapWebsitePreviewWeddingValuesToWebsiteDateProp(weddingValues, 'MMMM D, YYYY') ||
      mapWeddingAccountToWebsiteDateProp(Object.assign({}, wedding, account), 'MMMM D, YYYY');
    if (formData.title) {
      return (
        <PublicHeader
          slug={wedding.slug}
          name={formData.title}
          weddingDate={weddingDate}
          location={formData.location}
          hashtag={formData.hashtag}
          websiteDate={websiteDateProp}
        />
      );
    }

    let location = '';
    if (wedding.city && wedding.state_province) {
      location = `${wedding.city}, ${wedding.state_province}`;
    } else if (wedding.city) {
      location = `${wedding.city}`;
    }
    return (
      <PublicHeader
        slug={wedding.slug}
        name={wedding.title}
        weddingDate={weddingDate}
        location={location}
        hashtag={wedding.hashtag}
        websiteDate={websiteDateProp}
      />
    );
  };

  const renderPreviewContent = () => (
    <div
      className={cx('preview-container', { 'sticky-preview-container': showStickyPreview })}
      id="public-wrapper"
    >
      <div className="chrome-img" />
      {activePage && activePage.hidden && (
        <div id="page-hidden-banner" className="text-center">
          <p>
            <img
              src={previewImages('npHidden').default}
              className="margin-right-2x"
              alt="Not Visible Icon"
            />
            This page is hidden, visible only to you.
          </p>
        </div>
      )}
      <div className="preview-frame-container">
        <div className="preview-frame">
          <div className="sticky-nav">
            {wedding.slug && renderPublicHeader()}
            {wedding.slug && pages && <PublicPagesNav slug={wedding.slug} pages={liveNavPages} />}
          </div>
          <div id="public-page-content-wrapper">
            <Suspense fallback={null}>
              {CurrentPage && (
                <CurrentPage forPreview page={pageData} formData={eventFormData || formData} />
              )}
            </Suspense>
            {wedding.owner_first_name &&
              wedding.partner_first_name &&
              activePage &&
              activePage.type !== 'HOME' &&
              account.enable_seal && (
                <div className="container seal">
                  <div className="seal-section">
                    <span className="name-1">
                      {wedding.owner_first_name && (
                        <span className="initial-1">{wedding.owner_first_name[0]}</span>
                      )}
                      {wedding.owner_last_name && (
                        <span className="initial-2">{wedding.owner_last_name[0]}</span>
                      )}
                    </span>
                    {wedding.owner_first_name && wedding.partner_first_name && (
                      <span>
                        <span className="amp">&amp;</span>
                        <span className="and">and</span>
                        <span className="plus">+</span>
                      </span>
                    )}
                    <span className="name-2">
                      {wedding.partner_first_name && (
                        <span className="initial-1">{wedding.partner_first_name[0]}</span>
                      )}
                      {wedding.partner_last_name && (
                        <span className="initial-2">{wedding.partner_last_name[0]}</span>
                      )}
                    </span>
                  </div>
                </div>
              )}
          </div>
          {activePage && <PublicFooter page={activePage} />}
        </div>
      </div>
    </div>
  );

  return (
    <div
      className={cx('website-preview-v2', {
        'margin-top': !isHome,
      })}
    >
      {checkStyleObjectDataValid(defaultValues) &&
      checkStyleObjectDataValid(customizationValues) &&
      !hideCustomization ? (
        <StyledPublicWrapper
          className={styles.FontModuleWrapper}
          {...customizationValues}
          defaultValues={defaultValues}
        >
          {renderPreviewContent()}
        </StyledPublicWrapper>
      ) : (
        renderPreviewContent()
      )}
    </div>
  );
};

WebsitePreview.propTypes = {
  values: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    hidden: PropTypes.bool,
  }),
  activePage: PropTypes.shape({}),
  pages: PropTypes.arrayOf(PropTypes.shape({})),
  wedding: PropTypes.shape({}),
  account: PropTypes.shape({}),
  isHome: PropTypes.bool,
  weddingValues: PropTypes.shape({}),
  eventForm: PropTypes.shape({}), // only passed down by guest manager WebsitePreviewContainer to reuse component
  hideCustomization: PropTypes.bool,
};

export default WebsitePreview;
